import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { productName } from "../config";
import { formatDateTime } from "../lib/licenses";
import { monoFont } from "../theme";

const Key = ({ value }) => (
  <Box component="span" sx={{ fontFamily: monoFont, fontSize: "0.85em" }}>
    {value}
  </Box>
);

function describe(entry) {
  const what = `${productName(entry.product)} ${entry.type || ""}`.trim();
  const to = entry.distributorName ? ` to ${entry.distributorName}` : "";
  switch (entry.action) {
    case "issue":
      return (
        <>
          issued <Key value={entry.licenseKey} /> ({what}) for{" "}
          {entry.customer || "a customer"}
          {entry.distributorName ? ` · ${entry.distributorName}` : ""}
        </>
      );
    case "allocate":
      return entry.delta > 0
        ? `allocated ${entry.delta} ${what} keys${to}${entry.note ? ` — ${entry.note}` : ""}`
        : `took back ${-entry.delta} ${what} keys from ${entry.distributorName || "a distributor"}${
            entry.note ? ` — ${entry.note}` : ""
          }`;
    case "generate":
      return `generated ${entry.count} ${what}${entry.development ? " development" : ""} keys${
        entry.customer
          ? ` for ${entry.customer}`
          : entry.distributorName
            ? ` labelled ${entry.distributorName}`
            : ""
      }`;
    case "revoke":
      return (
        <>
          revoked <Key value={entry.licenseKey} /> ({what}) and returned it to{" "}
          {entry.distributorName || "the distributor"}
        </>
      );
    case "delete":
      return (
        <>
          deleted <Key value={entry.licenseKey} /> ({what})
        </>
      );
    case "extend":
      return (
        <>
          extended <Key value={entry.licenseKey} /> to {entry.to}
        </>
      );
    case "development":
      return entry.licenseKey ? (
        <>
          {entry.development ? "marked" : "unmarked"}{" "}
          <Key value={entry.licenseKey} /> as a development key
        </>
      ) : (
        `${entry.development ? "marked" : "unmarked"} ${entry.count} ${productName(entry.product)} keys as development keys`
      );
    case "grant":
      return `gave ${entry.email} ${entry.role === "admin" ? "admin" : "distributor"} access`;
    case "revokeAccess":
      return `removed portal access for ${entry.email}`;
    default:
      return entry.action;
  }
}

export default function ActivityList({
  entries,
  emptyText = "No activity yet.",
}) {
  if (!entries.length) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 3, color: "text.secondary", textAlign: "center" }}
      >
        {emptyText}
      </Paper>
    );
  }
  return (
    <Paper variant="outlined">
      {entries.map((entry, index) => (
        <Box
          key={entry.id}
          sx={{
            px: 2,
            py: 1.25,
            borderTop: index ? 1 : 0,
            borderColor: "divider",
            display: "flex",
            gap: 2,
            alignItems: "baseline",
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
            <strong>{entry.actor}</strong> {describe(entry)}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            {formatDateTime(entry.at)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}
